import { useFormikContext } from "collections/Form";
import { ModalConfirm, useModalDispatch } from "collections/Modal";
import {
  DesktopDeviceFormSettings,
  DESKTOP_DEVICE_FORM_SETTINGS,
  MY_SETTINGS_CHANGES_VIEW,
  MY_SETTINGS_POLICY_VIEW
} from "Constants";
import { CategoryDiv, CategorySwitcher } from "elements";
import { DeviceFragment } from "generated/graphql";
import { LocationDescriptorObject } from "history";
import {
  generatePath,
  Link,
  parse,
  stringify,
  useHistory,
  useQueryParamString
} from "polyrouter";
import React from "react";
import { useTranslation } from "utilities/i18n";
import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import {
  desktopDeviceSettingChanged,
  DesktopDeviceSettingsChanges,
  SettingChange
} from "store/desktopDeviceSettings";
import { ProtectedRoute } from "TsRoleGuards";
import "../Settings/MySettings.scss";
import { SettingValue } from "../Settings/ProvisionedDeviceSettingsRouter";
import { DesktopDeviceSettingsMapper } from "./DesktopDeviceSettingsMapper";
import { useInheritanceAndIdentifier } from "./hooks";
import {
  CapabilitiesMap,
  DesktopDevicePolicyScope,
  DevicePolicyInfo,
  useDesktopDevicePolicyScopeContext
} from "./ProvisionedDesktopDeviceSettingsRouter";
import { BasicDesktopDeviceSettings } from "./schemas/types";
import { generateC10String } from "./utilities/c10";

export const getUnsavedChangesCount = (
  settings: DesktopDeviceSettingsChanges
): number => {
  let count = 0;
  Object.values(settings).forEach(() => {
    count++;
  });
  return count;
};

export function useRevertSetting() {
  const { setFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  return (accessor: string, type?: string) => {
    dispatch(
      desktopDeviceSettingChanged({
        accessor,
        change: SettingChange.REVERTED
      })
    );

    let value: SettingValue = "";
    if (type === "boolean") {
      value = false;
    }
    const fieldName = generateC10String(accessor);
    // wrap in setTimeout to allow revert change to propagate to state (so it is ignored during validation)
    setTimeout(() => {
      // remove the value (set it to "" or to false in case of bool settings)
      setFieldValue(fieldName, value);
    }, 0);
  };
}

export function useDiscardSetting() {
  const { setFieldValue, getFieldMeta } = useFormikContext();
  const dispatch = useDispatch();

  return (accessor: string) => {
    // clear it from redux
    dispatch(
      desktopDeviceSettingChanged({
        accessor,
        change: ""
      })
    );
    // set it back to its original value
    const fieldName = generateC10String(accessor);
    const initialValue = getFieldMeta(fieldName).initialValue;
    setFieldValue(fieldName, initialValue);
  };
}

export type MyDesktopDeviceSettingsProps = {
  deviceSettings: BasicDesktopDeviceSettings;
  device?: DeviceFragment | DevicePolicyInfo;
  deviceModel: string;
  capabilitiesMap: CapabilitiesMap;
  site?: string;
  dirty: boolean;
  group?: string;
  userGroup?: string;
};

const getSettingsToDisplay = (
  unsavedChanges: DesktopDeviceSettingsChanges,
  capabilitiesMap: CapabilitiesMap,
  view: string
) => {
  const settingsToDisplay: DesktopDeviceFormSettings = {};
  const desktopDeviceFormSettingsCopy = { ...DESKTOP_DEVICE_FORM_SETTINGS };

  if (view === "changes") {
    const unsavedChangesAcessors = Object.keys(unsavedChanges);
    Object.entries(desktopDeviceFormSettingsCopy).forEach(
      ([category, categorySettings]) => {
        const settings = categorySettings.filter(categorySetting => {
          return unsavedChangesAcessors.includes(categorySetting.accessor);
        });

        if (settings.length) {
          settingsToDisplay[category] = settings;
        }
      }
    );
  }
  if (view === "policy" && capabilitiesMap) {
    const policySettingValues = Object.keys(capabilitiesMap);
    Object.entries(desktopDeviceFormSettingsCopy).forEach(
      ([category, categorySettings]) => {
        const settings = categorySettings.filter(categorySetting => {
          return policySettingValues.some(value => {
            let settingReverted = false;
            Object.entries(unsavedChanges).forEach(([accessor, change]) => {
              if (
                value.includes(accessor) &&
                change === SettingChange.REVERTED
              ) {
                settingReverted = true;
              }
            });
            return !settingReverted && value.includes(categorySetting.accessor);
          });
        });

        if (settings.length) {
          settingsToDisplay[category] = settings;
        }
      }
    );
  }
  return settingsToDisplay;
};

const MyDesktopDeviceSettings: React.FC<MyDesktopDeviceSettingsProps> = props => {
  const { t } = useTranslation();
  const [tab, setTab] = useQueryParamString<"view">("view");
  const unsavedChanges = useSelector(
    (state: State) => state.desktopDeviceSettings
  );
  const unsavedChangesCount = getUnsavedChangesCount(unsavedChanges);
  const [state] = useDesktopDevicePolicyScopeContext();

  const view = tab ?? MY_SETTINGS_POLICY_VIEW;

  const settingsToDisplay = getSettingsToDisplay(
    unsavedChanges,
    props.capabilitiesMap,
    view
  );

  const { policyIdentifier, inheritedBy } = useInheritanceAndIdentifier(
    state.desktopDevicePolicyScope,
    props
  );

  return (
    <>
      <div className="my-settings">
        <CategorySwitcher>
          <CategoryDiv
            label={t("POLICY")}
            active={view === MY_SETTINGS_POLICY_VIEW}
            onClick={() => setTab(MY_SETTINGS_POLICY_VIEW)}
            data-testid="policy-switcher"
          />
          <CategoryDiv
            label={t("CHANGES")}
            active={view === MY_SETTINGS_CHANGES_VIEW}
            onClick={() => setTab(MY_SETTINGS_CHANGES_VIEW)}
            data-testid="changes-switcher"
          />
        </CategorySwitcher>
        {unsavedChangesCount > 0 && (
          <span className="inline-block unsaved-changes-ellipse">
            {unsavedChangesCount}
          </span>
        )}
        <DesktopDeviceSettingsMapper
          {...props}
          view={view}
          inheritedBy={inheritedBy}
          settingsToDisplay={settingsToDisplay}
          showInheritance={
            state.desktopDevicePolicyScope === DesktopDevicePolicyScope.Device
          }
        />
      </div>
      <ProtectedRoute nest exact path="/remove" requirement="update:device">
        <RemoveAllSettingsModal
          settings={settingsToDisplay}
          policyIdentifier={policyIdentifier}
          desktopDevicePolicyScope={state.desktopDevicePolicyScope}
        />
      </ProtectedRoute>
    </>
  );
};

export const UnsavedChangesReviewMessage: React.FC<{
  unsavedChanges: number;
  deviceId?: string;
}> = ({ unsavedChanges, deviceId }) => {
  const { t } = useTranslation();
  const history = useHistory();

  if (unsavedChanges <= 0) {
    return null;
  }

  const MY_SETTINGS_PATH = "settings/my-settings";
  const pathnameUrl = deviceId
    ? generatePath(`/manage/inventory/details/${deviceId}/${MY_SETTINGS_PATH}`)
    : generatePath(`/manage/policy/details/${MY_SETTINGS_PATH}`);

  const queryParams: Record<"view", string> = {
    view: MY_SETTINGS_CHANGES_VIEW
  };
  const reviewUrl: LocationDescriptorObject = {
    pathname: pathnameUrl,
    search: stringify({
      ...parse(history.location.search),
      ...queryParams
    })
  };

  return (
    <p>
      <span className="review-changes-dialogue">
        {t("REVIEW_CHANGES_DIALOGUE", {
          unsavedChanges
        })}
      </span>
      <Link to={reviewUrl}>{t("REVIEW_CHANGES")}</Link>
    </p>
  );
};

export const RemoveAllSettingsModal: React.FC<{
  settings: DesktopDeviceFormSettings;
  policyIdentifier: string;
  desktopDevicePolicyScope: DesktopDevicePolicyScope | null;
}> = ({ settings, policyIdentifier, desktopDevicePolicyScope }) => {
  const { t } = useTranslation();
  const dispatchEvent = useModalDispatch();
  const revertSetting = useRevertSetting();
  const inheritance = useSelector(
    (state: State) => state.desktopDeviceSettingInheritance
  );

  const removeAll = () => {
    Object.entries(settings).forEach(([, categorySettings]) => {
      categorySettings.forEach(setting => {
        if (
          inheritance[generateC10String(setting.accessor)].scope ===
            desktopDevicePolicyScope ||
          desktopDevicePolicyScope !== DesktopDevicePolicyScope.Device
        ) {
          revertSetting(setting.accessor, setting.type);
        }
      });
    });

    dispatchEvent();
  };

  return (
    <ModalConfirm
      title={t("REMOVE_ALL_POLICY_SETTINGS")}
      onConfirm={removeAll}
      onCancel={dispatchEvent}
      open={true}
      message={t("REMOVE_ALL_POLICY_SETTINGS_CONFIRMATION", {
        name: policyIdentifier
      })}
      confirmLabel={t("OK")}
    />
  );
};

export default MyDesktopDeviceSettings;
